import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_FACE_SWAP_STATE,
  buildFaceSwapRequest,
  hasSourceImage,
  type FaceSwapFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { ReferenceImageInput } from '../references/ReferenceImageInput';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const FaceSwapPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<FaceSwapFormState>(DEFAULT_FACE_SWAP_STATE);
  const [targetRef, setTargetRef] = useState<SourceImageRef | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.faceSwap);

  const hasSource = hasSourceImage(source);
  const hasTargetRef = !!targetRef;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildFaceSwapRequest(state, sid, source, targetRef);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !hasTargetRef;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SwapHorizIcon fontSize="small" />
        <Typography variant="subtitle2">Face swap</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Source image: the photo whose face is being replaced.</Alert>
      )}

      <ReferenceImageInput
        label="Face reference"
        helperText="The face to use (donor)"
        value={targetRef}
        onChange={setTargetRef}
      />

      <Stack direction="row" spacing={1}>
        <TextField
          label="Source face #"
          type="number"
          value={state.sourceFaceIndex}
          onChange={e => setState(s => ({ ...s, sourceFaceIndex: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 0, max: 9, step: 1 }}
          helperText="0 = first face"
        />
        <TextField
          label="Donor face #"
          type="number"
          value={state.targetFaceIndex}
          onChange={e => setState(s => ({ ...s, targetFaceIndex: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 0, max: 9, step: 1 }}
          helperText="0 = first face"
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={state.enhanceFace}
            onChange={e => setState(s => ({ ...s, enhanceFace: e.target.checked }))}
            size="small"
          />
        }
        label="Enhance swapped face"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Face swap failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <SwapHorizIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Swapping…' : 'Swap face'}
        </Button>
      </Box>
    </Stack>
  );
};
